import React, { useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    IconButton,
    TableRow,
    TableCell,
    tableCellClasses,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Card,
    CardMedia,
    CardContent,
    CardActions,
    Chip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { showLoader } from '../../Redux/Slices/loaderSlice';
import { toast } from 'react-toastify';
import DataNotFound from '../../Components/Common/DataNotFound';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import axios from 'axios';
import { BaseURL, BucketURL } from '../../ApiSetup/Api';
import sessionStoreUtil from '../../ApiSetup/Localstorage';

const PostTemplates = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [templates, setTemplates] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);

    const getAuthHeaders = () => {
        const token = localStorage.getItem("auth_token");
        return { Authorization: token };
    };

    const fetchCategories = async () => {
        try {
            const response = await axios.post(
                `${BaseURL}admin/post-generator/category/get`,
                { includeInactive: false },
                { headers: getAuthHeaders() }
            );
            if (response?.data?.success) {
                setCategories(response?.data?.data || []);
            }
        } catch (error) {
            console.error('Fetch categories error:', error);
        }
    };

    const fetchTemplates = async () => {
        dispatch(showLoader(true));
        try {
            const payload = { includeInactive: true };
            if (selectedCategory) {
                payload.category = selectedCategory;
            }
            const response = await axios.post(
                `${BaseURL}admin/post-generator/template/get`,
                payload,
                { headers: getAuthHeaders() }
            );
            if (response?.data?.success) {
                setTemplates(response?.data?.data || []);
            }
        } catch (error) {
            console.error('Fetch templates error:', error);
            toast.error('Failed to fetch templates');
        } finally {
            dispatch(showLoader(false));
        }
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    useEffect(() => {
        fetchTemplates();
    }, [selectedCategory]);

    const handleDelete = async () => {
        if (!selectedTemplate) return;

        dispatch(showLoader(true));
        try {
            await axios.delete(
                `${BaseURL}admin/post-generator/template/${selectedTemplate._id}`,
                { headers: getAuthHeaders() }
            );
            toast.success('Template deleted successfully');
            setDeleteDialogOpen(false);
            setSelectedTemplate(null);
            fetchTemplates();
        } catch (error) {
            console.error('Delete error:', error);
            toast.error(error?.response?.data?.message || 'Failed to delete template');
        } finally {
            dispatch(showLoader(false));
        }
    };

    const openDeleteDialog = (template) => {
        setSelectedTemplate(template);
        setDeleteDialogOpen(true);
    };

    const openPreview = (template) => {
        setSelectedTemplate(template);
        setPreviewOpen(true);
    };

    const getImageUrl = (path) => {
        if (!path) return '/placeholder-image.png';
        if (path.startsWith('http')) return path;
        return `${BucketURL}${path}`;
    };

    return (
        <Box sx={{ p: 3 }}>
            <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h5" fontWeight="bold">
                        🎨 Post Templates
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Filter by Category</InputLabel>
                            <Select
                                value={selectedCategory}
                                label="Filter by Category"
                                onChange={(e) => setSelectedCategory(e.target.value)}
                            >
                                <MenuItem value="">All Categories</MenuItem>
                                {categories.map(cat => (
                                    <MenuItem key={cat._id} value={cat._id}>
                                        {cat.icon} {cat.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => navigate('/admin/post-generator/template/add')}
                        >
                            Add Template
                        </Button>
                    </Box>
                </Box>

                {templates.length > 0 ? (
                    <Grid container spacing={3}>
                        {templates.map((template) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={template._id}>
                                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <CardMedia
                                        component="img"
                                        height="200"
                                        image={getImageUrl(template.previewImage)}
                                        alt={template.name}
                                        sx={{ objectFit: 'cover', cursor: 'pointer' }}
                                        onClick={() => openPreview(template)}
                                    />
                                    <CardContent sx={{ flexGrow: 1 }}>
                                        <Typography variant="h6" gutterBottom noWrap>
                                            {template.name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            {template.nameGuj || '-'}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                                            <Chip
                                                label={template.category?.name || 'Unknown'}
                                                size="small"
                                                icon={<span>{template.category?.icon}</span>}
                                            />
                                            {template.isDefault && (
                                                <Chip label="Default" size="small" color="primary" />
                                            )}
                                            <Chip
                                                label={template.isActive ? 'Active' : 'Inactive'}
                                                size="small"
                                                color={template.isActive ? 'success' : 'default'}
                                            />
                                        </Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                            Usage: {template.usageCount || 0} times
                                        </Typography>
                                    </CardContent>
                                    <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                                        <Button
                                            size="small"
                                            startIcon={<VisibilityIcon />}
                                            onClick={() => openPreview(template)}
                                        >
                                            Preview
                                        </Button>
                                        <Box>
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => navigate(`/admin/post-generator/template/edit/${template._id}`)}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => openDeleteDialog(template)}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </CardActions>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <DataNotFound title="No templates found" />
                )}
            </Paper>

            {/* Preview Dialog */}
            <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md">
                <DialogTitle>{selectedTemplate?.name}</DialogTitle>
                <DialogContent>
                    <Box sx={{ textAlign: 'center' }}>
                        <img
                            src={getImageUrl(selectedTemplate?.previewImage)}
                            alt={selectedTemplate?.name}
                            style={{ maxWidth: '100%', maxHeight: '70vh' }}
                        />
                    </Box>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2">
                            <strong>Dimensions:</strong> {selectedTemplate?.width} x {selectedTemplate?.height}px
                        </Typography>
                        <Typography variant="body2">
                            <strong>Photo Config:</strong> {selectedTemplate?.photoConfig?.enabled ? 'Enabled' : 'Disabled'}
                            {selectedTemplate?.photoConfig?.enabled && ` (${selectedTemplate?.photoConfig?.shape})`}
                        </Typography>
                        <Typography variant="body2">
                            <strong>Name Config:</strong> {selectedTemplate?.nameConfig?.enabled ? 'Enabled' : 'Disabled'}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreviewOpen(false)}>Close</Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setPreviewOpen(false);
                            navigate(`/admin/post-generator/template/edit/${selectedTemplate._id}`);
                        }}
                    >
                        Edit Template
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Template</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete "{selectedTemplate?.name}"?
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDelete}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PostTemplates;
